import { LockKeyhole } from 'lucide-react';
import { motion } from 'motion/react';
import { Link } from 'react-router';
import { t } from '../i18n/index.ts';
import { useInviteOnly } from '../features/auth/Providers.tsx';
import { Brand } from '../layout/Brand.tsx';

/**
 * Portada: un abanico de cinco generaciones que se teje al cargar, con los tintes
 * de cada línea familiar. Es el único momento animado de la página.
 */
function WovenFan() {
  const rings = [
    { r0: 46, r1: 96, n: 2 },
    { r0: 100, r1: 150, n: 4 },
    { r0: 154, r1: 204, n: 8 },
    { r0: 208, r1: 258, n: 16 },
  ];
  const lineColor = (i: number, n: number) => ['var(--indigo)', 'var(--ochre)', 'var(--cochineal)', 'var(--moss)'][Math.floor((i / n) * 4)]!;
  const arc = (r0: number, r1: number, a0: number, a1: number) => {
    const p = (r: number, a: number) => `${300 + r * Math.cos(a)} ${290 + r * Math.sin(a)}`;
    const large = a1 - a0 > Math.PI ? 1 : 0;
    return `M${p(r1, a0)} A${r1} ${r1} 0 ${large} 1 ${p(r1, a1)} L${p(r0, a1)} A${r0} ${r0} 0 ${large} 0 ${p(r0, a0)} Z`;
  };
  const start = Math.PI * 0.85;
  const sweep = Math.PI * 1.3;
  return (
    <svg viewBox="0 0 600 420" className="w-full" role="img" aria-label={t('Abanico de cinco generaciones de una familia')}>
      {rings.map((ring, g) =>
        Array.from({ length: ring.n }).map((_, i) => {
          const a0 = start + (sweep / ring.n) * i;
          const a1 = a0 + sweep / ring.n;
          return (
            <motion.path
              key={`${g}-${i}`}
              d={arc(ring.r0, ring.r1, a0 + 0.012, a1 - 0.012)}
              fill={lineColor(i, ring.n)}
              initial={{ opacity: 0, scale: 0.92 }}
              animate={{ opacity: 0.18 + g * 0.2, scale: 1 }}
              transition={{ delay: 0.15 + g * 0.18 + i * 0.015, duration: 0.6, ease: 'easeOut' }}
              style={{ transformOrigin: '300px 290px' }}
            />
          );
        }),
      )}
      <circle cx="300" cy="290" r="40" fill="var(--pine)" />
      <text x="300" y="296" textAnchor="middle" fill="var(--on-pine)" fontFamily="var(--font-serif)" fontSize="17" fontWeight="600">{t('Tú')}</text>
    </svg>
  );
}

export function LandingPage() {
  const inviteOnly = useInviteOnly();
  return (
    <div className="min-h-dvh">
      <header className="mx-auto flex max-w-6xl items-center justify-between px-5 py-5">
        <Brand />
        <nav className="flex items-center gap-2">
          <Link to="/entrar" className="rounded-full px-4 py-2 text-[15px] hover:bg-surface-2">{t('Entrar')}</Link>
          <Link to="/registro" className="rounded-full bg-pine px-4 py-2 text-[15px] font-medium text-on-pine">{t('Crear cuenta')}</Link>
        </nav>
      </header>
      <main className="mx-auto grid max-w-6xl items-center gap-10 px-5 pb-16 pt-6 md:grid-cols-[1fr_1.1fr] md:pt-14">
        <div>
          <h1 className="text-[40px] leading-[1.05] md:text-[58px]">{t('El árbol de tu familia, tejido entre todos.')}</h1>
          <p className="mt-5 max-w-lg text-[17px] text-ink-soft">
            {t('Invita a tus familiares con un código QR. Cada uno se encuentra en el árbol en menos de dos minutos, completa su rama y descubre cómo está emparentado con los demás.')}
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/registro" className="rounded-full bg-pine px-6 py-3 text-base font-medium text-on-pine shadow-soft">
              {inviteOnly ? t('Crear mi cuenta') : t('Empezar el árbol de mi familia')}
            </Link>
            <Link to="/entrar" className="rounded-full border border-line bg-surface px-6 py-3 text-base">{t('Ya tengo cuenta')}</Link>
          </div>
          {inviteOnly && (
            <p className="mt-5 flex max-w-lg items-start gap-2 text-[15px] text-ink-soft">
              <LockKeyhole className="mt-0.5 size-4 shrink-0" aria-hidden />
              {t('Para entrar al árbol de tu familia necesitas una invitación: pide el enlace o el código QR a un familiar que ya esté en él.')}
            </p>
          )}
        </div>
        <WovenFan />
      </main>
      <section className="border-t border-line bg-surface">
        <div className="mx-auto grid max-w-6xl gap-8 px-5 py-12 md:grid-cols-3">
          <div>
            <h2 className="text-xl">{t('Unirse con un QR')}</h2>
            <p className="mt-2 text-[15px] text-ink-soft">{t('Escribes tu nombre y el de tus padres; si ya están en el árbol, los confirmas con un toque.')}</p>
          </div>
          <div>
            <h2 className="text-xl">{t('Saber qué son')}</h2>
            <p className="mt-2 text-[15px] text-ink-soft">{t('Al conocer a un familiar, escanea su QR: “primos terceros, ambos bisnietos de Manuel Gallarday”.')}</p>
          </div>
          <div>
            <h2 className="text-xl">{t('Nada se pierde')}</h2>
            <p className="mt-2 text-[15px] text-ink-soft">{t('Cada corrección queda registrada con su autor y puede deshacerse. Partidas y fotos quedan guardadas en privado.')}</p>
          </div>
        </div>
      </section>
    </div>
  );
}
